/**
 * Modern CTA card with kicker + title + subtitle + CTA pill.
 * Ported from ThemeMaker's {@code blocks/cta/CtaCardModern}.
 *
 * <h3>Accent variants</h3>
 * <ul>
 *   <li>{@code brand} — brand-500 → brand-700 gradient, dark text white
 *       (default)</li>
 *   <li>{@code dark}  — fixed dark gradient (matches FeatureSpotlight)</li>
 *   <li>{@code light} — fixed light gradient with dark text</li>
 * </ul>
 *
 * <p><b>Why hardcoded colours, not theme tokens:</b> this card has its
 * own internal contrast story — an "always orange brand card" should
 * look the same in dark + light mode (the brand is the brand).
 * Using {@code --color-text-primary} caused the CTA pill text to flip
 * to white in dark mode, landing white-text-on-white-pill (invisible).
 * Brand tokens ({@code brand-500}, {@code brand-700}) ARE used because
 * brand colours legitimately vary per-tenant, but brand colours don't
 * flip with mode.
 */
import type { CSSProperties } from 'react';
import { Card } from '@akademi/ui';
import { tx } from '../_shared/coerce';
import { sectionPad, sectionInner, kickerStyle, cardReset } from '../_shared/styles';
import { PortalLink } from '@akademi/site-builder-runtime';
import { BlockRoot } from '../_shared/BlockRoot';

type Localized = string | Record<string, string>;

export interface CtaCardModernProps {
  kicker?: Localized;
  title?: Localized;
  subtitle?: Localized;
  ctaLabel?: Localized;
  ctaHref?: string;
  secondaryLinks?: Array<{ label: Localized; href: string }>;
  accent?: 'brand' | 'dark' | 'light';
}

export function CtaCardModern({
  kicker,
  title,
  subtitle,
  ctaLabel,
  ctaHref,
  secondaryLinks = [],
  accent = 'brand',
}: CtaCardModernProps) {
  const isLight = accent === 'light';
  const cardStyles: CSSProperties = isLight
    ? {
        // Fixed light gradient — locks the "light island on the page"
        // visual whether the page is light or dark.
        background: 'linear-gradient(135deg, #eef2ff, #e0e7ff)',
        color: 'var(--color-text-primary, #18181b)',
      }
    : accent === 'dark'
      ? {
          // Fixed dark gradient.
          background: 'linear-gradient(135deg, #18181b, #3f3f46)',
          color: 'var(--color-text-on-brand, #fff)',
        }
      : {
          // Brand gradient — brand tokens are stable across mode.
          background:
            'linear-gradient(135deg, var(--color-brand-500, #3b5bdb), var(--color-brand-700, #1d4ed8))',
          color: 'var(--color-text-on-brand, #fff)',
        };

  return (
    <BlockRoot as="section" style={sectionPad}>
      <div style={sectionInner}>
        <Card
          className="cta-card-modern"
          style={{
            ...cardReset,
            ...cardStyles,
            padding: 'clamp(32px, 4vw, 56px)',
            borderRadius: 'var(--radius-lg, 12px)',
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr) auto',
            alignItems: 'center',
            gap: 'var(--space-8, 32px)',
          }}
        >
          <div>
            {kicker && (
              <div
                style={{
                  ...kickerStyle,
                  color: isLight ? '#1d4ed8' : 'rgba(255,255,255,0.7)',
                  marginBottom: 'var(--space-3, 12px)',
                }}
              >
                {tx(kicker)}
              </div>
            )}
            <div
              style={{
                fontSize: 'clamp(24px, 2.6vw, 36px)',
                fontWeight: 600,
                lineHeight: 1.15,
                fontFamily: 'var(--font-family-display, inherit)',
                letterSpacing: 'var(--font-tracking-snug, -0.01em)',
              }}
            >
              {tx(title) || 'Ready to get started?'}
            </div>
            {subtitle && (
              <div
                style={{
                  marginTop: 'var(--space-3, 12px)',
                  fontSize: 'var(--font-size-md, 16px)',
                  lineHeight: 1.5,
                  opacity: isLight ? 0.7 : 0.85,
                  maxWidth: 520,
                }}
              >
                {tx(subtitle)}
              </div>
            )}
            {secondaryLinks.length > 0 && (
              <div
                style={{
                  marginTop: 'var(--space-5, 20px)',
                  display: 'flex',
                  gap: 'var(--space-5, 20px)',
                  flexWrap: 'wrap',
                }}
              >
                {secondaryLinks.map((l, i) => (
                  <PortalLink
                    key={i}
                    href={l.href}
                    style={{
                      color: 'inherit',
                      fontSize: 'var(--font-size-sm, 14px)',
                      fontWeight: 500,
                      textDecoration: 'none',
                      borderBottom: `1px solid ${isLight ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.4)'}`,
                      paddingBottom: 2,
                    }}
                  >
                    {tx(l.label)} →
                  </PortalLink>
                ))}
              </div>
            )}
          </div>
          {ctaLabel && (
            <PortalLink
              href={ctaHref ?? '#'}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 10,
                padding: 'var(--space-3-5, 14px) var(--space-7, 28px)',
                // CTA pill: hardcoded so it stays inverse of card body text
                // regardless of page mode (see top-of-file note).
                background: isLight
                  ? 'var(--color-text-primary, #18181b)'
                  : 'var(--color-neutral-0, #fff)',
                color: isLight
                  ? 'var(--color-text-on-brand, #fff)'
                  : 'var(--color-text-primary, #18181b)',
                borderRadius: 'var(--radius-pill, 9999px)',
                fontSize: 'var(--font-size-md, 16px)',
                fontWeight: 600,
                textDecoration: 'none',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              {tx(ctaLabel)} <span aria-hidden>→</span>
            </PortalLink>
          )}
        </Card>
      </div>
      <style
        dangerouslySetInnerHTML={{
          __html: `
        @media (max-width: 720px) {
          .cta-card-modern {
            grid-template-columns: 1fr !important;
          }
        }
      `,
        }}
      />
    </BlockRoot>
  );
}
